/** Fleet/devices page (artifacts/web/src/pages/fleet.tsx) + fleet components. */
export const fleet = {
  page: {
    title: "Fleet Status",
    description: "Real-time status of all player devices.",
    newDevice: "New Device",
    searchPlaceholder: "Search by name, location, or platform…",
    loadError: {
      title: "Couldn't load devices",
      description:
        "Something went wrong while loading your fleet. Check your connection and try again.",
    },
    empty: {
      noMatch: "No devices match your filters.",
      title: "Add your first screen",
      none: "Claim a screen and activate it to start playing your starter station, or try OmniMedia on the screen you're looking at. No install needed.",
      addScreen: "Add a screen",
    },
  },

  // "Enter code" pairing (CB-060): type the short code shown on a signed-out
  // screen to pair it without scanning a QR.
  pair: {
    enterCode: "Enter code",
    title: "Pair a screen by code",
    description:
      "Open OmniMedia on the screen, then type the code it shows and choose which device it becomes.",
    codeLabel: "Pairing code",
    codePlaceholder: "A3F9 C2D1",
    deviceLabel: "Pair as device",
    devicePlaceholder: "Choose a device",
    noDevices: "Add a device first, then pair a screen to it.",
    submit: "Pair screen",
    success: "Screen paired. It will start playing shortly.",
    error: "Couldn't pair that screen",
  },

  // Status filter options unique to this page (Online/Offline reuse common.status).
  filter: {
    allStatuses: "All statuses",
    idle: "Idle",
    error: "Error",
    allLocations: "All locations",
  },

  // Cross-cutting placeholder values for missing/unknown data.
  value: {
    unknown: "Unknown",
    unassigned: "Unassigned",
    never: "Never",
  },

  card: {
    nowPlaying: "Now Playing",
    status: "Status",
    settings: "Settings",
  },

  confirm: {
    deleteDevice: "Delete device?",
  },

  entitlement: {
    required: "An active subscription is required. Subscribe on Billing to continue",
  },

  toast: {
    deviceDeleted: "Device deleted",
    couldNotDeleteDevice: "Could not delete device",
    deviceActivated: "Device activated",
    deviceDeactivated: "Device deactivated",
    couldNotUpdateDevice: "Could not update device",
    couldNotUpdateFeatures: "Could not update features",
    couldNotUpdateAnnouncementTone: "Could not update announcement tone",
    couldNotUpdateOfflineSettings: "Could not update offline settings",
    couldNotUpdateDisplaySettings: "Could not update display settings",
    notAllowedChannel: "Not allowed to change this channel",
    couldNotUpdateChannels: "Could not update channels",
    deviceRegistered: "Device registered",
    couldNotRegisterDevice: "Could not register device",
    tryAgain: "Please try again.",
    deviceUpdated: "Device updated",
    failedToUpdate: "Failed to update",
    exitPinUpdated: "Exit PIN updated",
    exitPinCleared: "Exit PIN cleared",
    couldNotUpdateExitPin: "Could not update exit PIN",
    couldNotClearExitPin: "Could not clear exit PIN",
  },

  // Admin action buttons (device-admin-actions.tsx).
  actions: {
    heading: "Actions",
    offlineHint:
      "Player not connected. Commands will apply when it comes back online.",
    logout: "Logout",
    quit: "Quit",
    forceSync: "Force sync",
    restart: "Restart",
    clearCache: "Clear cache",
    // Confirmation dialogs before dispatching irreversible commands.
    confirm: {
      logout: {
        title: "Log out this player?",
        body: "\"{name}\" will be signed out and its screen credential revoked. It stops playing until someone claims it again on the device.",
      },
      quit: {
        title: "Quit the player app?",
        body: "\"{name}\" will exit player mode and stop playing until the player is opened again on the device.",
      },
      clearCache: {
        title: "Clear this player's cache?",
        body: "Deletes all media \"{name}\" has downloaded for offline playback. It re-downloads what it needs while online, but offline playback is degraded until then.",
      },
      restart: {
        title: "Restart this player?",
        body: "\"{name}\" will reload and resume its schedule automatically. Playback pauses for a few seconds.",
      },
    },
  },

  // Diagnostics panel (device-diagnostics-panel.tsx).
  label: {
    heartbeat: "Heartbeat",
    version: "Version",
    volume: "Volume",
    cache: "Cache",
    platform: "Platform",
    location: "Location",
    deviceId: "Device ID",
    state: "State",
    reason: "Reason",
    channel: "Channel",
    playlist: "Playlist",
    track: "Track",
    artist: "Artist",
  },
  section: {
    device: "Device",
    offlineReadiness: "Offline readiness",
    nowPlaying: "Now playing",
    onNow: "On now",
    lastError: "Last error",
    lastCommand: "Last command",
    recentActivity: "Recent activity",
  },

  // What the device itself reports about its local media cache. The split that
  // matters is "playing now" vs "look ahead": a device whose current program is
  // fully cached survives an outage starting this second even while it is still
  // filling the rest of the window.
  offlineCache: {
    ready: "Offline ready",
    caching: "Caching {cached} of {total}",
    notReady: "Not offline ready",
    onlineOnly: "Online only",
    status: "Status",
    playingNow: "Playing now",
    lookAhead: "Look ahead",
    filesOf: "{cached} of {total} files",
    byKind: "Media {media} of {mediaTotal}, artwork {artwork} of {artworkTotal}",
    used: "Cache used",
    usedValue: "{used} MB of {total} MB",
    overLimit: "Over the size limit",
    reported: "Reported",
    asOf: "as of {when}",
    neverReported: "No cache report yet",
    onlineOnlyNote: "The browser player streams and keeps no local cache.",
    missing: "Still downloading",
    missingMore: "and {count} more",
    neededNow: "Needed now",
  },
  diagnostics: {
    noPlayback: "No live playback reported.",
    noErrors: "No errors reported.",
    noCommands: "No commands dispatched yet.",
    noEvents: "No recent events.",
    noDetails: "No details.",
  },
  // "Reason" the current content is playing, per channel type.
  reason: {
    music: "Music",
    ads: "Advertisement",
    announcements: "Announcement",
    video: "Video / Signage",
  },

  // On-now schedule strip (device-on-now-schedule.tsx).
  onNow: {
    playing: "Playing",
    silent: "Silent",
    nextAt: "Next: {name} at {time}",
    noSchedule: "No schedule resolved for this device.",
  },

  // Preview-on-this-screen flow (preview-this-screen.tsx).
  preview: {
    deviceName: "This screen (preview)",
    label: "Preview on this screen",
    successTitle: "This screen is now a player",
    successDescription: "Opening the player in a new tab.",
    noLicenseTitle: "No device license available",
    failedTitle: "Preview failed",
    licenseHint: "{message} Manage licenses on the Billing page.",
  },

  // Device settings dialog (fleet.tsx).
  settings: {
    registerTitle: "Register New Device",
    title: "Device Settings",
    deviceName: "Device Name",
    locationOptional: "Location (Optional)",
    assignPlaceholder: "Assign to a location",
    unknownLocation: "Unknown location",
    inactiveNote:
      "This device is inactive. Content won't play until you activate it.",
    deviceActive: "Device active",
    deviceActiveHint:
      "Active devices play scheduled content. Inactive devices stay registered but do not play.",
    enabledChannels: "Enabled channels",
    enabledChannelsHint:
      "Choose which content types this device plays. All channels are included in your device license.",
    premiumFeatures: "Premium features",
    premiumFeaturesHint: "Included with your device license.",
    liveBroadcast: "Live broadcast",
    liveBroadcastHint:
      "Enable on devices that should receive live announcements. Staff go live with Hold to talk on the",
    controlPage: "Control page",
    djMode: "DJ Mode",
    kioskMode: "Kiosk mode",
    kioskModeHint:
      "Lock this device into a full-screen player with no on-device controls. Leave off to run it as a normal window the venue can move and multitask around (recommended for PCs). A kiosk can still be exited with the exit PIN.",
    announcementTone: "Announcement tone",
    announcementToneHint:
      "Short attention chime played on the device before announcements. Pick Custom sound to use one of your own uploads (max {seconds}s).",
    previewToneAria: "Preview tone",
    display: "Display",
    displayHint:
      "How content renders on this screen: signage fit, physical rotation, and zoom.",
    fitMode: "Fit mode",
    rotation: "Rotation",
    zoom: "Zoom",
    transition: "Music transitions",
    transitionHint:
      "Auto follows DJ Mode. Crossfade blends song endings on the music channel; ads and announcements always cut cleanly.",
    crossfadeDuration: "Crossfade length",
    syncGroup: "Sync group",
    syncGroupHint:
      "Devices sharing a group id drift-lock to the same timeline. Leave empty to sync with the device's location.",
    syncGroupPlaceholder: "Location default",
    outputVolume: "Output volume",
    outputVolumeHint:
      "Master volume for this device. Saved to the device, so it survives player restarts.",
    outputVolumeAria: "Device output volume",
    muteDevice: "Mute all audio",
    muteDeviceAria: "Mute all audio on this device",
    offlinePlayback: "Offline playback",
    offlinePlaybackHint:
      "How far ahead the desktop, TV, and mobile player apps cache the schedule and media for offline playback, and how much disk they may use.",
    cacheWindow: "Cache window",
    cacheSizeLimit: "Cache size limit",
    kioskExitPin: "Kiosk exit PIN",
    kioskExitPinHint: "Optional PIN required to leave player mode on this screen.",
    pinDraftWillSave: "PIN will be saved when you register this device.",
    pinNoneSet: "No exit PIN is set. Switch Mode will not ask for a code.",
    pinConfigured:
      "A PIN is configured on this device. Enter a new PIN to change it, or clear it below.",
    pinPlaceholderChange: "Enter new PIN to change",
    pinPlaceholderNew: "4–8 digits (optional)",
    savePin: "Save PIN",
    clearPin: "Clear PIN",
    registerDevice: "Register device",
    deleteDevice: "Delete device",
  },

  // Announcement-tone select options.
  tone: {
    off: "Off",
    ding: "Ding",
    chime: "Chime",
    bell: "Bell",
    customSound: "Custom sound",
    customSoundOption: "Custom sound…",
  },

  // Video-fit / display select options.
  transition: {
    auto: "Auto (follow DJ Mode)",
    standard: "Standard (clean cuts)",
    crossfade: "Crossfade",
    seconds: "{sec} seconds",
  },

  videoFit: {
    cover: "Fill screen (crop to fit)",
    contain: "Fit entire image (letterbox)",
  },

  // Screen rotation select options (degrees clockwise).
  orientation: {
    deg0: "Landscape (default)",
    deg90: "Portrait (90° clockwise)",
    deg180: "Upside down (180°)",
    deg270: "Portrait (90° counter-clockwise)",
  },

  // Content zoom select options.
  zoom: {
    defaultOption: "100% (default)",
    option: "{pct}%",
  },

  // Offline cache select options.
  offline: {
    day1: "1 day",
    days3: "3 days",
    days7: "7 days (default)",
    days14: "14 days",
    gb1: "1 GB",
    gb4: "4 GB (default)",
    gb16: "16 GB",
    gb64: "64 GB",
  },

  // Custom announcement-tone picker dialog.
  tonePicker: {
    title: "Custom announcement tone",
    intro:
      "Use one of your uploaded sounds as the attention tone. Tones must be {seconds} seconds or shorter. Announcement uploads are listed first.",
    empty:
      "No audio uploads yet. Upload a short audio file in the Library (Announcements) first.",
    previewAria: "Preview {title}",
    unknownLength: "unknown length",
    overLimit: " (over the {seconds}s limit)",
    lengthRequired: " (length required)",
    selected: "Selected",
    use: "Use",
  },

  // Player build identity reported by the device itself (devices.clientInfo,
  // rendered by components/fleet/device-build-info.tsx): which bundle a venue
  // is actually running, and whether its updater still works. The app version
  // alone cannot answer that, because an over-the-air update replaces the
  // bundle without moving the binary version.
  build: {
    section: "Build",
    shell: "Shell",
    appVersion: "App version",
    buildNumber: "Build number",
    runtimeVersion: "Runtime",
    update: "Update",
    channel: "Channel",
    published: "Published",
    embedded: "Installed build",
    embeddedHint: "No over-the-air update applied.",
    lastCheck: "Last update check",
    reported: "Reported",
    noReport: "This device has not reported a build yet.",
    status: {
      ok: "Updater healthy",
      pendingRestart: "Update waiting for restart",
      failing: "Update failing",
      unsupported: "Updates on reload",
      unknown: "Updater not reported",
    },
    failingSince: "Update failing since the check at {when}",
    failingUnknownSince: "Update failing, no check time reported",
    error: "Updater error: {message}",
  },

  // Per-channel display labels used inside fleet UI (distinct wording from the
  // shared `channels` vocabulary — e.g. "Advertisements", "Video / Signage").
  channelLabel: {
    music: "Music",
    ads: "Advertisements",
    announcements: "Announcements",
    video: "Video / Signage",
  },
} as const;
